import { Box, Divider, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { ReactElement, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { CardHeaderFlex } from '../../component/cardHeaderFlex/CardHeaderFlex';
import Skeleton from '../../component/skeleton/Skeleton';
import { getDeploymentStats, getItemTimeline, itemDeploymentStats, itemDeploymentStatsFetching, itemTimeline } from '../../redux/slices/tools/toolsSlice';
import { useDispatch } from '../../redux/store';
import { CardFlex } from '../../styled/components';

type ItemUtilizationProps = { itemId: string };

// How hard this one item has worked over the last year: the share of days it
// spent at a job site, and the shape of those stays. Same reconstruction the
// dashboard uses, so an item, its product and the organization all measure
// deployment the same way; internal base and staging sites do not count.
export const ItemUtilization = ({ itemId }: ItemUtilizationProps): ReactElement => {
  const dispatch = useDispatch();
  const stats = useSelector(getDeploymentStats(itemId));
  const stays = useSelector(getItemTimeline(itemId));
  const fetching = useSelector(itemDeploymentStatsFetching);
  const theme = useTheme();

  useEffect(() => {
    dispatch(itemDeploymentStats({ itemId }));
    dispatch(itemTimeline(itemId));
  }, [dispatch, itemId]);

  // The year as a strip: a filled block for every stay at a job site, the bare
  // track behind it for the time the item spent at home. Positions are
  // percentages of the window, so the strip fills whatever width it is given
  // without measuring anything.
  const strip = useMemo(() => {
    const start = DateTime.now().startOf('day').minus({ years: 1 });
    const span = DateTime.now().startOf('day').diff(start, 'days').days || 1;
    const blocks = (stays || []).map((stay) => {
      const from = DateTime.fromISO(stay.from_date || '');
      const to = DateTime.fromISO(stay.to_date || '');
      const left = Math.max(0, from.diff(start, 'days').days / span) * 100;
      const right = Math.min(1, to.diff(start, 'days').days / span) * 100;
      return {
        key: `${stay.site_id}-${stay.from_date}`,
        left,
        // a one-day stay still needs to be visible
        width: Math.max(0.4, right - left),
        label: `${stay.site_name || 'Job site'}: ${from.toLocaleString(DateTime.DATE_MED)} to ${to.toLocaleString(DateTime.DATE_MED)}`,
      };
    });
    const months = Array.from({ length: 13 }, (unused, index) => start.plus({ months: index })).map((date) => ({
      key: date.toISO() || '',
      left: (date.diff(start, 'days').days / span) * 100,
      label: date.toFormat('LLL'),
    }));
    return { blocks, months };
  }, [stays]);

  const rows = [
    { label: 'Days at job sites', value: stats ? `${(stats.days_onsite || 0).toLocaleString()} of 366` : '—' },
    { label: 'Deployments', value: stats ? (stats.deployments || 0).toLocaleString() : '—' },
    { label: 'Sites visited', value: stats ? (stats.distinct_sites || 0).toLocaleString() : '—' },
    { label: 'Avg stay', value: stats ? `${stats.avg_days ?? 0} days` : '—' },
    { label: 'Longest stay', value: stats ? `${stats.max_days ?? 0} days` : '—' },
  ];

  return (
    <CardFlex style={{ display: 'flex', flexDirection: 'column' }}>
      <CardHeaderFlex title={<Skeleton width={60} fetching={fetching} value={stats ? `${stats.pct_of_year ?? 0}%` : '—'} />} legend="Utilization & Usage" />
      <Stack sx={{ p: 4, pt: 3, flexGrow: 1 }}>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Share of the past 12 months at a job site
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Divider sx={{ mb: 2 }} />

        {rows.map((row) => (
          <Stack key={row.label} direction="row" justifyContent="space-between" sx={{ py: 0.75 }}>
            <Typography variant="body2" color="textSecondary">
              {row.label}
            </Typography>
            <Typography variant="body2" fontWeight="fontWeightBold">
              {row.value}
            </Typography>
          </Stack>
        ))}

        <Box sx={{ mt: 3 }}>
          <Typography variant="caption" color="textSecondary">
            At a job site
          </Typography>
          <Box sx={{ position: 'relative', height: 26, mt: 0.5, backgroundColor: 'action.hover', borderRadius: 0.5, overflow: 'hidden' }}>
            {strip.blocks.map((block) => (
              <Tooltip key={block.key} title={block.label} placement="top">
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${block.left}%`,
                    width: `${block.width}%`,
                    backgroundColor: theme.palette.primary.main,
                  }}
                />
              </Tooltip>
            ))}
          </Box>
          <Box sx={{ position: 'relative', height: 16, mt: 0.5 }}>
            {strip.months.map((month, index) => (
              <Typography
                key={month.key}
                variant="caption"
                sx={{ position: 'absolute', left: `${month.left}%`, transform: index ? 'translateX(-50%)' : 'none', color: 'text.secondary', lineHeight: 1 }}
              >
                {month.label}
              </Typography>
            ))}
          </Box>
        </Box>
      </Stack>
    </CardFlex>
  );
};
